import {
	type BlokFetcherProps,
	type BlokProps,
	StoryblokComponent,
	StoryblokComponentList,
} from "@trydig/storyblok";
import { classed, clsx } from "@trydig/ui";
import { notFound } from "next/navigation";
import { rpc } from "#rpc";
import { BlokWrap } from "@/components/bloks/BlokWrap";
import { BreadcrumbsSkeleton } from "@/components/ui/Breadcrumbs";
import { Container } from "@/components/ui/Container";
import { Skeleton } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/ui/sections/PageHeader";
import { Typography } from "@/components/ui/Typography";
import {
	Filterbar,
	FilterbarSkeleton,
} from "@/features/products/ProductListingPage/components/Filterbar";
import {
	ProductList,
	ProductListSkeleton,
} from "@/features/products/ProductListingPage/components/ProductList";
import { ProductListingSchema } from "@/features/products/ProductListingPage/components/ProductListingSchema";
import { PLPProvider } from "@/features/products/ProductListingPage/hooks/PLPContext";
import type { PLPPage } from "@/features/products/ProductListingPage/types";
import type { PageHeaderStoryblok, ProductListingPageStoryblok } from "@/generated/sb/sb-types";
import { capitalizeFirstLetter } from "@/lib/utils/capitalizeFirstLetter";

type PLPExtraProps = {
	slug?: string[];
	/**
	 * Optional override for the Centra category id. When the PLP is rendered
	 * from the EcomLayer `category:` branch we have the resolved Centra
	 * category id; otherwise fall back to `blok.centraCategory` from the story.
	 */
	categoryId?: string;
	/**
	 * Forwarded from `Page.tsx` so the JSON-LD schema can build canonical URLs
	 * without re-reading cookies here — `$geo()` would error out on static /
	 * prefetch render paths where the request scope isn't available.
	 */
	language?: string;
	country?: string;
};

const EMPTY_PAGE: PLPPage = {
	products: [],
	total: 0,
	nextPageParam: null,
	filters: [],
	sorts: [],
	source: "depict",
	error: null,
};

export const productListingPageFetcher = async ({
	blok,
	preview,
}: BlokFetcherProps<ProductListingPageStoryblok>): Promise<PLPPage | null> => {
	const depictCategory = blok.depictCategory as string | undefined;
	const centraCategory = blok.centraCategory as string | undefined;
	if (!depictCategory && !centraCategory) return null;
	// `preview` lets the source fall back to the Norway geo when the preview
	// iframe has no Centra cookie — see `geoOrDefault`.
	return rpc.plp.list({ depictCategory, centraCategory, preview });
};

export const productListingPageKey = ({ blok }: BlokFetcherProps<ProductListingPageStoryblok>) => [
	String(blok.depictCategory ?? ""),
];

export const ProductListingPageBlok = ({
	blok,
	preview,
	data,
	slug,
	categoryId,
	language,
	country,
}: BlokProps<ProductListingPageStoryblok, PLPPage | null> & PLPExtraProps) => {
	const depictCategory = blok.depictCategory as string | undefined;

	if (!depictCategory) {
		console.error("NOT FOUND - MISSING DEPICT CATEGORY (ProductListingPageBlok)");
		notFound();
	}

	const initialPage: PLPPage = data ?? EMPTY_PAGE;
	const centraCategoryId = categoryId ?? (blok.centraCategory as string | undefined) ?? "";

	// Breadcrumbs derived from the URL slug (same logic as the previous inline header).
	const breadcrumbs = slug?.map((label, i) => {
		const isLast = i === slug.length - 1;
		const href = isLast ? undefined : `/${slug.slice(0, i + 1).join("/")}`;
		return { label: capitalizeFirstLetter(label).replaceAll("-", " "), href };
	});

	// PageHeader renders header image cards through its `collections` slot. The PLP
	// stores these as `categoryCard` bloks, which are field-for-field identical to
	// `pageHeaderCollection` apart from two renamed keys — map them across, spreading
	// the original blok so Storyblok's `_editable` metadata is preserved.
	const collections = blok.cards?.map((card) => ({
		...card,
		component: "pageHeaderCollection",
		image: card.asset,
		actionLabel: card.linkLabel,
	})) as PageHeaderStoryblok["collections"];

	return (
		<BlokWrap editable={blok} as="main" className="bg-brand-sand-10">
			{language && country && slug && (
				<ProductListingSchema
					products={initialPage.products}
					language={language}
					country={country}
					slug={slug}
					name={blok.header}
				/>
			)}
			<PageHeader
				size="level-1"
				breadcrumbs={breadcrumbs}
				title={blok.header}
				description={blok.preamble}
				collections={collections}
				preview={preview}
			/>
			<Container className="flex flex-col py-grid-section-vertical-lg gap-grid-section-vertical-lg">
				<PLPProvider
					initialPage={initialPage}
					categorySources={{
						depictCategoryId: depictCategory,
						centraCategoryId,
					}}
					preview={preview}
				>
					<Filterbar />
					<ProductList
						content={blok.body?.map((card, i) => {
							const isEven = i % 2 === 0;

							// Mobile: First card at row 4, then 2 rows gap between cards (always spans 2 rows)
							let mobileRowStart = 4;
							mobileRowStart += i * 4;

							const currentMobileRows = 2;
							const mobileRowEnd = mobileRowStart + 2;

							// Desktop: First card at row 3, then 1 row gap between cards
							let desktopRowStart = 3;
							for (let j = 0; j < i; j++) {
								const prevCardRows = Number(blok.body?.[j]?.desktopRows || "1");
								desktopRowStart += prevCardRows + 1;
							}

							const currentDesktopRows = Number(card.desktopRows || "1");
							const desktopRowEnd = desktopRowStart + currentDesktopRows;

							const desktopColumns = Number(card.desktopColumns || "2");

							return {
								mobileRowStart,
								mobileRows: currentMobileRows,
								desktopRowStart,
								element: (
									<div
										key={i}
										className={clsx("featured-content col-span-2 min-h-0 h-full")}
										style={
											{
												"--mobile-row-start": mobileRowStart,
												"--mobile-row-end": mobileRowEnd,
												"--desktop-row-start": desktopRowStart,
												"--desktop-row-end": desktopRowEnd,
												"--desktop-col-start": isEven
													? `calc(var(--grid-cols) - ${desktopColumns} + 1)`
													: 1,
												"--desktop-col-end": isEven
													? `calc(var(--grid-cols) + 1)`
													: desktopColumns + 1,
											} as React.CSSProperties
										}
									>
										<StoryblokComponent blok={card} preview={preview} className="h-full w-full" />
									</div>
								),
							};
						})}
					/>
				</PLPProvider>
			</Container>
			{blok.bottom && <StoryblokComponentList bloks={blok.bottom} preview={preview} />}
		</BlokWrap>
	);
};

export const ProductListingPageBlokSkeleton = ({
	blok,
}: BlokProps<ProductListingPageStoryblok>) => {
	return (
		<BlokWrap editable={blok} as="main" className="bg-brand-sand-10">
			<Header>
				<BreadcrumbsSkeleton />
				<Skeleton fitContent>
					<Typography
						variant="heading-display-lg"
						className="mt-grid-section-vertical-md mb-grid-section-vertical-lg"
					>
						{blok.header}
					</Typography>
				</Skeleton>
				<Skeleton fitContent>
					<Typography variant="body-sm-reg" className="mb-grid-section-vertical-lg">
						{blok.preamble}
					</Typography>
				</Skeleton>
			</Header>
			<Container className="flex flex-col py-grid-section-vertical-lg gap-grid-section-vertical-lg">
				<FilterbarSkeleton />
				<ProductListSkeleton />
			</Container>
		</BlokWrap>
	);
};

const Header = classed(
	Container,
	"pt-grid-section-vertical-md flex flex-col bg-background-bg-sand-1",
);
